import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertIcon, ClockIcon, WifiOffIcon, CheckIcon, InfoIcon, EyeIcon, EyeOffIcon,
  MailBig, KeyBig, TeamBig, ClockBig, OkBig, BrokenBig,
} from './icons';
import { MIN_PASSWORD } from './validators';
import { WHATSAPP } from './AuthShell';

const NOTE_ICONS = { alert: AlertIcon, clock: ClockIcon, wifi: WifiOffIcon, check: CheckIcon, info: InfoIcon };
const HEAD_ICONS = { mail: MailBig, key: KeyBig, team: TeamBig, clock: ClockBig, ok: OkBig, broken: BrokenBig };

/** Inline banner (replaces the bottom toasts on the auth pages). pair = [title, body]. */
export function Banner({ kind, icon, pair, extra, children }) {
  const Icon = NOTE_ICONS[icon] || AlertIcon;
  const ref = useRef(null);
  // A banner can appear while the form is scrolled out from under it (long
  // signup form on a phone, answer to a tap on the submit button at the bottom).
  useEffect(() => {
    const el = ref.current;
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, []);
  return (
    <div ref={ref} className={`ppa-note ${kind}`} role={kind === 'crit' ? 'alert' : 'status'}>
      <Icon />
      <div><strong>{pair[0]}</strong>{pair[1]}{extra}{children}</div>
    </div>
  );
}

export function Head({ icon, tone, title, text, children }) {
  const Icon = icon ? HEAD_ICONS[icon] : null;
  return (
    <div className="ppa-head">
      {Icon && <div className={`ppa-icon ${tone || ''}`.trim()}><Icon /></div>}
      <h1>{title}</h1>
      <p>{text}</p>
      {children}
    </div>
  );
}

export function Submit({ busy, busyLabel, children, disabled, onClick, type = 'submit', outline, id }) {
  return (
    <button
      id={id}
      className={`ppa-btn${busy ? ' busy' : ''}${outline ? ' outline' : ''}`}
      type={type}
      disabled={disabled}
      aria-busy={busy ? 'true' : undefined}
      onClick={onClick}
    >
      {busy ? (<><span className="ppa-spin" aria-hidden="true" />{busyLabel}</>) : children}
    </button>
  );
}

const errIcon = <AlertIcon size={15} />;

export function Field({
  id, label, type = 'text', ltr, autoComplete = 'off', placeholder, value, onChange, error,
  readOnly, aside, after, inputRef, autoFocus, name, inputMode, invalid, onKeyUp,
}) {
  const { t } = useTranslation();
  const [shown, setShown] = useState(false);
  const pw = type === 'password';
  const bad = !!error || invalid;
  return (
    <div className="ppa-field">
      <div className="ppa-lbl-row"><label htmlFor={id}>{label}</label>{aside}</div>
      <div className={`ppa-ctrl${pw ? ' pw' : ''}`}>
        <input
          id={id}
          name={name || id}
          ref={inputRef}
          type={pw && shown ? 'text' : type}
          dir={ltr ? 'ltr' : undefined}
          value={value}
          onChange={onChange}
          onKeyUp={onKeyUp}
          placeholder={placeholder}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          inputMode={inputMode}
          readOnly={readOnly}
          aria-invalid={bad ? 'true' : undefined}
          aria-describedby={`${id}-e`}
          autoCapitalize="off"
          spellCheck={false}
        />
        {pw && (
          <button
            type="button"
            className="ppa-eye"
            aria-label={t(`authPage.${shown ? 'hide' : 'show'}`)}
            aria-pressed={shown}
            onClick={() => setShown((s) => !s)}
          >
            {shown ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        )}
      </div>
      <span className="ppa-err" id={`${id}-e`} hidden={!error}>{error ? <>{errIcon}{error}</> : null}</span>
      {after}
    </div>
  );
}

/** Password input with Caps Lock warning and (optional) live "8+" checklist. */
export function PasswordField({ id, label, value, onChange, error, autoComplete, aside, checklist, inputRef, autoFocus }) {
  const { t } = useTranslation();
  const [caps, setCaps] = useState(false);
  const onKey = (e) => { if (e.getModifierState) setCaps(e.getModifierState('CapsLock')); };
  return (
    <Field
      id={id}
      label={label}
      type="password"
      autoComplete={autoComplete}
      value={value}
      onChange={onChange}
      error={error}
      aside={aside}
      inputRef={inputRef}
      autoFocus={autoFocus}
      onKeyUp={onKey}
      after={(
        <>
          {caps && <span className="ppa-caps" role="status"><AlertIcon size={15} />{t('authPage.caps')}</span>}
          {checklist && (
            <ul className="ppa-reqs" aria-label={t('authPage.req8')}>
              <li className={value.length >= MIN_PASSWORD ? 'ok' : undefined}><i />{t('authPage.req8')}</li>
            </ul>
          )}
        </>
      )}
    />
  );
}

/** Splits a translated string on {{m}} so a styled node can be dropped in. */
export function withNode(str, node, token = '{{m}}') {
  const [a, b = ''] = String(str).split(token);
  return (<>{a}{node}{b}</>);
}


/**
 * Banner for the failures every auth request can share: offline, rate-limited
 * (with a live countdown) and "something broke on our side". `left` is the
 * seconds remaining for the rate-limit case.
 */
export function ErrorBanner({ kind, left }) {
  const { t } = useTranslation();
  const tr = (k) => t(`authPage.login.${k}`, { returnObjects: true, s: '{{m}}' });
  if (kind === 'offline') return <Banner kind="crit" icon="wifi" pair={tr('network')} />;
  if (kind === 'rate') {
    const pair = tr('locked');
    return <Banner kind="warn" icon="clock" pair={[pair[0], withNode(pair[1], <b>{left}</b>)]} />;
  }
  // 'server' and anything unrecognised: never blank.
  return (
    <Banner
      kind="crit"
      icon="alert"
      pair={tr('server')}
      extra={<div className="ppa-note-extra">{t('authPage.foot')} <span className="ppa-num" dir="ltr">{WHATSAPP}</span></div>}
    />
  );
}

/** Moves focus to the first invalid control once the error state has rendered. */
export function focusFirstInvalid(root) {
  setTimeout(() => {
    const el = root?.querySelector?.('[aria-invalid="true"]');
    if (el) el.focus();
  }, 0);
}
